/**
 * useCourse Hook
 *
 * Fetches a single academy course with exam details using URQL.
 */

import { useQuery } from "urql";
import type { AcademyCourse } from "./useCourses";

// =============================================================================
// QUERY DOCUMENT
// =============================================================================

const AcademyCourseDocument = `
  query AcademyCourse($id: ID!) {
    academyCourse(id: $id) {
      id
      externalId
      title
      description
      externalUrl
      imageUrl
      sortOrder
      exam {
        id
        name
        description
        passingScore
        questionsCount
        currentQuestion {
          id
          questionNumber
          totalQuestions
          text
          options {
            id
            text
          }
        }
        currentAttempt {
          id
          score
          maxScore
          percentage
          passed
          totalWrongAttempts
          startedAt
          completedAt
        }
      }
      userProgress {
        visited
        visitCount
        examStarted
        examPassed
        examPassedAt
      }
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

export interface ExamOption {
  id: string;
  text: string;
}

export interface ExamQuestionView {
  id: string;
  questionNumber: number;
  totalQuestions: number;
  text: string;
  options: ExamOption[];
}

export interface ExamAttempt {
  id: string;
  score: number;
  maxScore: number;
  percentage: number;
  passed: boolean;
  totalWrongAttempts: number;
  startedAt: string;
  completedAt: string | null;
}

export interface CourseExam {
  id: string;
  name: string;
  description: string | null;
  passingScore: number;
  questionsCount: number;
  currentQuestion: ExamQuestionView | null;
  currentAttempt: ExamAttempt | null;
}

export interface CourseWithExam extends Omit<AcademyCourse, "exam"> {
  exam: CourseExam | null;
}

interface UseCourseOptions {
  pause?: boolean;
}

interface UseCourseResult {
  course: CourseWithExam | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Fetch a single academy course with exam details.
 */
export function useCourse(
  courseId: string,
  options: UseCourseOptions = {}
): UseCourseResult {
  const { pause = false } = options;

  const [result, reexecuteQuery] = useQuery<{ academyCourse: CourseWithExam | null }>({
    query: AcademyCourseDocument,
    variables: { id: courseId },
    pause: pause || !courseId,
  });

  return {
    course: result.data?.academyCourse ?? null,
    isLoading: result.fetching,
    error: result.error ?? null,
    refetch: () => reexecuteQuery({ requestPolicy: "network-only" }),
  };
}
